<template>
    <router-view></router-view>
</template>

<script setup>
import { onMounted, onBeforeUnmount } from 'vue';
import { ElMessage } from "element-plus"
import request from "@/request/request"
import { useTabbarStore } from "./pinia/tabbar"
import storage from "@/storage/storage"
import { useRouter, useRoute } from 'vue-router';
import {useUserDataStore} from "./pinia/userData"
import { Place, Location, Position, House, Notebook, OfficeBuilding, School, Picture, Ship, Shop, Coin, Tickets, PieChart } from '@element-plus/icons-vue'
import { setDynamicRoutesLoaded } from '@/router/router.config'

const router = useRouter()
const route = useRoute()
const userData = useUserDataStore()

const iconMap = {
  Place,
  Location,
  Position,
  House,
  Notebook,
  OfficeBuilding,
  School,
  Picture,
  Ship,
  Shop,
  Coin,
  Tickets,
  PieChart
}

const getIconByName = (icon) => {
  if (typeof icon === 'string') {
    return iconMap[icon] || House
  }
  if (icon && typeof icon.name === 'string') {
    return iconMap[icon.name] || House
  }
  return House
}

const handleRouteChange = (to) => {
  if (to.path === '/login') return
  
  const tabbarStore = useTabbarStore()
  const tabName = to.path === '/homePage' ? '首页' : ''
  
  let label = tabName
  let icon = House
  
  tabbarStore.getTabbar.forEach(group => {
    group.permits.forEach(item => {
      if (item.path === to.path) {
        label = item.label
        icon = getIconByName(item.icon)
      }
    })
  })
  
  if (label) {
    tabbarStore.addTab({
      path: to.path,
      label: label,
      icon: icon
    })
  }
}

onMounted(() => {
  const AutoLogin = async() => {
    const tabbarStore = useTabbarStore()
    let user = storage.ls.get("user")
    if(user == null) return
    
    user = JSON.parse(user)
    request.post('/autoLogin').then(res => {
      tabbarStore.setTabbar(res.data.components)
      userData.setUser(res.data.adminLoginData)
      ElMessage.success("欢迎回来·" + res.data.adminLoginData.name)
      
      // 收集所有需要添加的动态路由
      const dynamicRoutes = []
      res.data.components.forEach(item => {
        item.permits.forEach(i2 => {
          dynamicRoutes.push({
            path: i2.path,
            name: i2.path,
            component: () => import(`/src/${i2.component}.vue`).then(comp => {
              if (comp.default && !comp.default.name) {
                comp.default.name = i2.path
              }
              return comp
            })
          })
        })
      })
      
      // 添加所有动态路由
      dynamicRoutes.forEach(route => {
        router.addRoute("root", route)
      })
      
      // 标记动态路由已加载完成
      setDynamicRoutesLoaded(true)
      
      // 保持当前路径，如果当前路径无效则跳首页
      const currentPath = route.path
      const targetPath = currentPath && currentPath !== '/login' && currentPath !== '/' 
        ? currentPath 
        : '/homePage'
      
      router.replace({ path: targetPath }).then(() => {
        handleRouteChange({ path: targetPath })
      })
    }).catch(err => {
      const tabbarStore = useTabbarStore()
      tabbarStore.clearTabs()
      storage.ls.clear()
      setDynamicRoutesLoaded(false)
      router.replace({ path: "/login" })
    })
  }
  
  AutoLogin()
  
  router.afterEach(handleRouteChange)
})

onBeforeUnmount(() => {
  router.afterEach(() => {})
})
</script>

<style>
#app {
  height: 100%;
  width: 100%;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #334155;
  background: #f0f4f8;
  font-size: 14px;
  line-height: 1.6;
}

body, html {
  height: 100%;
  width: 100%;
  overflow: hidden;
  background: #f0f4f8;
  color: #334155;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

* {
  padding: 0;
  margin: 0;
  box-sizing: border-box;
}

::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: rgba(148, 163, 184, 0.05);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.2);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(148, 163, 184, 0.4);
}

/* ============ 全局移动端适配 ============ */

/* 平板端 */
@media (max-width: 1024px) {
  #app {
    font-size: 13px;
  }
}

/* 移动端 */
@media (max-width: 767px) {
  #app {
    font-size: 13px;
  }

  /* Element Plus 表格横向滚动 */
  .el-table {
    width: 100% !important;
    overflow-x: auto;
    
    .el-table__body-wrapper {
      overflow-x: auto !important;
    }
  }

  /* Element Plus 表单单列布局 */
  .el-form--inline .el-form-item {
    width: 100%;
    margin-right: 0;
    margin-bottom: 12px;
  }

  /* Element Plus 对话框全屏 */
  .el-dialog {
    width: 92% !important;
    margin: 5vh auto !important;
  }

  /* Element Plus 抽屉全宽 */
  .el-drawer {
    width: 85% !important;
  }

  /* Element Plus 分页器紧凑 */
  .el-pagination {
    .el-pagination__sizes,
    .el-pagination__jump {
      display: none;
    }
    
    .el-pager li {
      min-width: 28px;
      height: 28px;
      line-height: 28px;
    }
    
    button {
      min-width: 28px;
      height: 28px;
      line-height: 28px;
    }
  }

  /* Element Plus 卡片内边距减小 */
  .el-card__body {
    padding: 12px;
  }

  /* Element Plus 按钮间距 */
  .el-button + .el-button {
    margin-left: 6px;
  }

  /* 筛选区域 flex 换行 */
  .filter-content {
    flex-wrap: wrap;
    
    .filter-item {
      margin-bottom: 8px;
    }
  }

  /* 统计卡片网格 - 默认2列 */
  .stats-grid {
    grid-template-columns: repeat(2, 1fr) !important;
    gap: 10px !important;
  }

  /* 卡片标题缩小 */
  .section-title {
    font-size: 14px;
  }
}

/* 小屏手机 */
@media (max-width: 480px) {
  /* 统计卡片网格 - 单列 */
  .stats-grid {
    grid-template-columns: 1fr !important;
  }

  /* 筛选区域垂直排列 */
  .filter-content {
    flex-direction: column;
    align-items: stretch;

    .filter-actions {
      margin-left: 0;
      justify-content: flex-end;
    }
  }

  /* Element Plus 对话框接近全屏 */
  .el-dialog {
    width: 96% !important;
    margin: 3vh auto !important;
  }
}
</style>
